import React, { useEffect, useState } from "react";
import api from "../api/axios";
import toast from "react-hot-toast";

export default function AdminTransfersPage() {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingAction, setProcessingAction] = useState("");

  useEffect(() => {
    const run = async () => {
      try {
        const { data } = await api.get("/admin/bank/pending");
        setTransfers(Array.isArray(data) ? data : []);
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load transfer queue");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, []);

  async function approve(id) {
    if (processingAction) return;
    // eslint-disable-next-line no-restricted-globals
    const providerReference = window.prompt("Enter bank reference (e.g. TXN123456):");
    if (!providerReference) return;

    setProcessingAction(`${id}:approve`);
    try {
      const { data } = await api.patch(`/admin/bank/${id}/approve`, {
        providerReference,
        notes: "Paid manually",
      });
      toast.success(data.message || "Transfer approved");
      setTransfers((t) => t.filter((x) => String(x._id) !== String(id)));
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to approve transfer");
    } finally {
      setProcessingAction("");
    }
  }

  async function reject(id) {
    if (processingAction) return;
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm("Reject this transfer?")) return;

    setProcessingAction(`${id}:reject`);
    try {
      const { data } = await api.patch(`/admin/bank/${id}/reject`, { reason: "Rejected by admin" });
      toast.success(data.message || "Transfer rejected");
      setTransfers((t) => t.filter((x) => String(x._id) !== String(id)));
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to reject transfer");
    } finally {
      setProcessingAction("");
    }
  }

  const pendingCount = transfers.length;
  const totalPendingAmount = transfers.reduce((sum, tr) => sum + (Number(tr.amount) || 0), 0);

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-4 md:p-6">
      <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 text-white shadow-2xl shadow-cyan-500/10 backdrop-blur">
        <p className="text-xs uppercase tracking-[0.3em] text-cyan-200/80">Admin Queue</p>
        <h2 className="mt-2 text-2xl font-semibold md:text-3xl">Pending Bank Transfers</h2>
        <p className="mt-2 text-sm text-slate-300">
          Review payout requests, confirm external bank references, and keep transfer settlement clean.
        </p>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-5 text-white shadow-xl shadow-black/20 backdrop-blur">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Pending Requests</p>
          <p className="mt-2 text-2xl font-semibold">{pendingCount}</p>
        </div>
        <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-5 text-white shadow-xl shadow-black/20 backdrop-blur">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Total Pending Amount</p>
          <p className="mt-2 text-2xl font-semibold">₦{totalPendingAmount.toLocaleString()}</p>
        </div>
        <div className="rounded-3xl border border-cyan-300/20 bg-cyan-500/10 p-5 text-cyan-100 shadow-xl shadow-cyan-900/30 backdrop-blur">
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-200/80">Status</p>
          <p className="mt-2 text-2xl font-semibold">{loading ? "Loading" : "Ready"}</p>
        </div>
      </section>

      <section className="rounded-[2rem] border border-white/10 bg-slate-900/70 p-5 text-white shadow-2xl shadow-black/30 backdrop-blur md:p-6">
        <div className="space-y-3">
          {loading && <div className="rounded-2xl border border-white/10 bg-slate-800/60 p-4 text-slate-300">Loading queue...</div>}
          {!loading && transfers.length === 0 && (
            <div className="rounded-2xl border border-white/10 bg-slate-800/60 p-4 text-slate-300">No transfers</div>
          )}
          {transfers.map((tr) => (
            <div
              key={tr._id}
              className="flex flex-col gap-4 rounded-2xl border border-white/10 bg-slate-800/60 p-4 shadow-lg shadow-black/20 md:flex-row md:items-center md:justify-between"
            >
              <div>
                <div className="font-semibold text-white">{tr.accountName} - ₦{Number(tr.amount || 0).toLocaleString()}</div>
                <div className="text-sm text-slate-300">
                  {tr.bankName} • {tr.accountNumber}
                </div>
                <div className="mt-1 text-xs text-slate-400">{tr.wallet?.user?.fullName || "Unknown user"}</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => approve(tr._id)}
                  disabled={Boolean(processingAction)}
                  aria-busy={processingAction === `${tr._id}:approve`}
                  className="rounded-xl border border-emerald-400/40 bg-emerald-500/20 px-3 py-1.5 text-sm font-medium text-emerald-100 transition hover:bg-emerald-500/30"
                >
                  {processingAction === `${tr._id}:approve` ? "Approving…" : "Approve"}
                </button>
                <button
                  onClick={() => reject(tr._id)}
                  disabled={Boolean(processingAction)}
                  aria-busy={processingAction === `${tr._id}:reject`}
                  className="rounded-xl border border-rose-400/40 bg-rose-500/20 px-3 py-1.5 text-sm font-medium text-rose-100 transition hover:bg-rose-500/30"
                >
                  {processingAction === `${tr._id}:reject` ? "Rejecting…" : "Reject"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
